import { api, apiBaseUrl } from './client';
import { textoDe } from '../i18n/textos';
import { textosCarta } from '../i18n/modulos/carta';

export type TipoPlato = 'sencillo' | 'elaborado';

export interface ApiProducto {
  id: number;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  /** Mig. 061: lo que muestra la tienda web en inglés. */
  descripcion_en?: string | null;
  categoria: 'alimentos' | 'bebidas' | 'otros';
  subcategoria: string | null;
  precio: number;
  inc_porcentaje: number;
  iva_porcentaje: number;
  activo: boolean;
  imagen_url: string | null;
  tipo_plato: TipoPlato;
  created_at: string;
  /** ¿Este plato tiene algo que quitar o adicionar? (mig. 033)
   *  Viene calculado en GET /api/menu para que el POS no tenga que preguntar
   *  plato por plato: con esto decide si mostrar el link "personalizar" en la
   *  comanda y si la línea se puede agrupar. */
  tiene_personalizacion?: boolean;
  /** ¿Tiene upsell configurado en Carta? */
  tiene_sugerencias?: boolean;
  /** Mig. 059: tiene grupos de opciones OBLIGATORIOS (Trifásico, Menú de niños).
   *  El POS abre la ventana de opciones al agregarlo. */
  tiene_modificadores?: boolean;
  /** ¿Está marcado como recomendado del día HOY en esta sede? (mig. 036)
   *  Solo viene cuando se pide GET /api/menu con sede_id. */
  recomendado_hoy?: boolean;
}

// Línea de receta enviada al crear/editar un plato elaborado.
export interface RecetaLineaInput {
  item_id: number;
  cantidad: number;
  unidad: string;
  notas?: string | null;
  /** Personalización (mig. 033). OMITIR el campo = "no opino, dejá lo que hay".
   *  Mandarlo explícito SÍ pisa el valor guardado — así se puede desactivar una
   *  adición. El backend conserva lo previo cuando llega `undefined`, porque
   *  antes un guardado de precio borraba toda la personalización del plato. */
  excluible?: boolean;
  /** Precio de la porción extra de este ingrediente en ESTE plato.
   *  null o 0 = no se ofrece como adición. */
  precio_adicion?: number | null;
}

export interface MenuQuery {
  categoria?: 'alimentos' | 'bebidas' | 'otros';
  subcategoria?: string;
  activo?: boolean;
  /** Filtrar disponibilidad por sede. Si el usuario está pinneado a una sede,
   *  el backend lo aplica automáticamente como defensa en profundidad. */
  sede_id?: number | null;
}

export const listMenu = (q: MenuQuery = {}) => {
  const params = new URLSearchParams();
  if (q.categoria) params.set('categoria', q.categoria);
  if (q.subcategoria) params.set('subcategoria', q.subcategoria);
  if (q.activo !== undefined) params.set('activo', String(q.activo));
  if (q.sede_id) params.set('sede_id', String(q.sede_id));
  const qs = params.toString();
  return api.get<ApiProducto[]>(`/api/menu${qs ? `?${qs}` : ''}`);
};

export const getProducto = (id: number) =>
  api.get<ApiProducto>(`/api/menu/${id}`);

/** Disponibilidad. Con `sedeId` se agota sólo en esa sede (producto_sedes);
 *  con `{ todasLasSedes: true }` se cambia el catálogo completo. */
export const toggleProductoActivo = (
  id: number,
  activo: boolean,
  opts: { sedeId?: number | null; todasLasSedes?: boolean } = {},
) =>
  api.patch<{ id: number; activo: boolean; sede_id: number | null; alcance: 'sede' | 'todas' }>(
    `/api/menu/${id}/activo`,
    { activo, sede_id: opts.sedeId ?? undefined, todas_las_sedes: opts.todasLasSedes || undefined },
  );

export interface ProductoInput {
  // Opcional al crear: si no se manda, el backend genera el código automáticamente
  // a partir de la subcategoría (ej. ENT-001, PIT-002).
  codigo?: string;
  nombre: string;
  descripcion?: string | null;
  descripcion_en?: string | null;
  categoria: 'alimentos' | 'bebidas' | 'otros';
  subcategoria?: string | null;
  precio: number;
  inc_porcentaje?: number;
  iva_porcentaje?: number;
  imagen_url?: string | null;
  // Nuevo: tipo del plato. 'elaborado' requiere `receta` con al menos 1 línea.
  tipo_plato?: TipoPlato;
  receta?: RecetaLineaInput[];
}

export const createProducto = (input: ProductoInput) =>
  api.post<ApiProducto>(`/api/menu`, input);

export const updateProducto = (id: number, input: Partial<ProductoInput>) =>
  api.put<ApiProducto>(`/api/menu/${id}`, input);

// =============================================================
// Modificadores (adiciones / salsas / extras) — fase A del POS Sicar
// =============================================================
export interface ModificadorOpcion {
  id: number;
  modificador_id: number;
  nombre: string;
  precio_extra: number;
  orden: number;
  activo: boolean;
  /** Receta de la opción (mig. 059): lo que se descuenta cada vez que se elige. */
  receta?: OpcionRecetaLinea[];
}

export interface OpcionRecetaLinea {
  opcion_id: number;
  item_id: number;
  cantidad: number;
  unidad: string;
  nombre: string;
  codigo: string;
  tipo: 'insumo' | 'compuesto';
  item_unidad: string;
}

export interface ModificadorGrupo {
  id: number;
  producto_id: number;
  nombre: string;             // "Salsa", "Cocción"
  obligatorio: boolean;
  min_select: number;
  max_select: number;
  /** Mig. 059: se puede elegir la misma opción varias veces (Trifásico: 3 de pollo). */
  permite_repetir?: boolean;
  orden: number;
  opciones: ModificadorOpcion[];
}

/**
 * Grupo obligatorio SIN repetición que pide más opciones de las que hay
 * ("elige 13 distintas" con 3 opciones): nadie lo puede completar y el POS se
 * queda trabado en "Faltan 10". Devuelve { pide, hay } o null si está bien.
 */
export function grupoSinSalida(g: ModificadorGrupo): { pide: number; hay: number } | null {
  if (g.permite_repetir) return null;
  const pide = Math.max(Number(g.min_select) || 0, g.obligatorio ? 1 : 0);
  const hay = g.opciones.filter(o => o.activo !== false).length;
  if (hay === 0 || pide <= hay) return null;
  return { pide, hay };
}

// Lo que se envía en el item del pedido cuando el cliente elige opciones
export interface ModificadorSeleccion {
  modificador_id: number;
  nombre: string;
  /** `veces` solo cuando el grupo permite repetir (mig. 059). Sin `veces` = 1. */
  opciones: Array<{ opcion_id: number; nombre: string; precio_extra: number; veces?: number }>;
}

export const listModificadores = (productoId: number) =>
  api.get<ModificadorGrupo[]>(`/api/menu/${productoId}/modificadores`);

export const crearModificador = (productoId: number, body: {
  nombre: string; obligatorio?: boolean; min_select?: number; max_select?: number; orden?: number; permite_repetir?: boolean;
}) => api.post<ModificadorGrupo>(`/api/menu/${productoId}/modificadores`, body);

export const editarModificador = (id: number, patch: Partial<{
  nombre: string; obligatorio: boolean; min_select: number; max_select: number; orden: number; permite_repetir: boolean;
}>) => api.patch<ModificadorGrupo>(`/api/menu/modificadores/${id}`, patch);

export const borrarModificador = (id: number) =>
  api.del<{ ok: true }>(`/api/menu/modificadores/${id}`);

export const crearOpcion = (modificadorId: number, body: {
  nombre: string; precio_extra?: number; orden?: number;
}) => api.post<ModificadorOpcion>(`/api/menu/modificadores/${modificadorId}/opciones`, body);

export const editarOpcion = (id: number, patch: Partial<{
  nombre: string; precio_extra: number; orden: number; activo: boolean;
}>) => api.patch<ModificadorOpcion>(`/api/menu/opciones/${id}`, patch);

export const borrarOpcion = (id: number) =>
  api.del<{ ok: true }>(`/api/menu/opciones/${id}`);

/** Reemplaza la receta de una opción (mig. 059). Cantidad en la unidad del item. */
export const guardarRecetaOpcion = (opcionId: number, lineas: Array<{ item_id: number; cantidad: number }>) =>
  api.put<{ ok: true; count: number }>(`/api/menu/opciones/${opcionId}/receta`, { lineas });

// --- Sugerencias / upsell (mig. 033) ----------------------------------------
/** Otro producto de la carta que combina con este. El mesero las ve al
 *  agregar el plato al pedido. */
export interface ProductoSugerencia {
  id: number;
  codigo: string;
  nombre: string;
  precio: number;
  subcategoria: string | null;
  imagen_url: string | null;
  nota: string | null;
  orden: number;
}

export const listSugerencias = (productoId: number) =>
  api.get<ProductoSugerencia[]>(`/api/menu/${productoId}/sugerencias`);

export const putSugerencias = (
  productoId: number,
  sugerencias: Array<{ sugerido_id: number; nota?: string }>,
) => api.put<{ ok: true; count: number }>(`/api/menu/${productoId}/sugerencias`, { sugerencias });

// ============================================================================
// RECOMENDADO DEL DÍA (mig. 036)
// ============================================================================

export interface RecomendadoDia {
  id: number;
  producto_id: number;
  sede_id: number;
  fecha: string;
  nota: string | null;
  creado_por_nombre: string | null;
  codigo: string;
  nombre: string;
  categoria: string;
  subcategoria: string | null;
  precio: number;
  imagen_url: string | null;
}

/** Candidato a recomendado, ordenado por el insumo más viejo que consume. */
export interface SugeridoRecomendado {
  id: number;
  codigo: string;
  nombre: string;
  categoria: string;
  subcategoria: string | null;
  precio: number;
  imagen_url: string | null;
  /** Nombre del insumo más viejo que usa el plato. */
  insumo_nombre: string | null;
  /** Días que ese insumo lleva en bodega. null si nunca se registró su entrada. */
  dias_en_bodega: number | null;
  sin_entrada_registrada: boolean;
  insumos_en_bodega: number;
  ya_recomendado: boolean;
}

export const fetchRecomendados = (sedeId: number, fecha?: string) =>
  api.get<{ fecha: string; sede_id: number; recomendados: RecomendadoDia[] }>(
    `/api/menu/recomendados?sede_id=${sedeId}${fecha ? `&fecha=${fecha}` : ''}`,
  );

/** "¿Qué conviene recomendar hoy?" — platos cuyo insumo lleva más en bodega. */
export const fetchSugeridosRecomendado = (sedeId: number, limit = 8) =>
  api.get<{ sede_id: number; fecha: string; sugeridos: SugeridoRecomendado[] }>(
    `/api/menu/recomendados/sugeridos?sede_id=${sedeId}&limit=${limit}`,
  );

export const marcarRecomendado = (producto_id: number, sede_id: number, nota?: string, fecha?: string) =>
  api.post<RecomendadoDia>('/api/menu/recomendados', { producto_id, sede_id, nota, fecha });

export const desmarcarRecomendado = (producto_id: number, sede_id: number, fecha?: string) =>
  api.del<{ ok: true; eliminados: number }>(
    `/api/menu/recomendados/${producto_id}?sede_id=${sede_id}${fecha ? `&fecha=${fecha}` : ''}`,
  );

// ============================================================================
// FOTOS DE LOS PLATOS (mig. 037)
// ============================================================================

/**
 * URL absoluta de la foto del plato.
 *
 * `productos.imagen_url` guarda una ruta RELATIVA al API
 * (`/api/menu/12/imagen?v=abc`). Un `<img src>` con esa ruta la resolvería
 * contra el origen del FRONTEND, que en dev y en prod es otro host — la foto
 * saldría rota. Por eso se antepone el base del API.
 *
 * Una URL absoluta (un CDN puesto a mano) se devuelve tal cual.
 */
export function urlImagenPlato(imagenUrl?: string | null): string | null {
  if (!imagenUrl) return null;
  if (/^(https?:)?\/\//i.test(imagenUrl) || imagenUrl.startsWith('data:')) return imagenUrl;
  return `${apiBaseUrl}${imagenUrl}`;
}

/** Tope del backend. Si se supera, responde 413. */
export const MAX_IMAGEN_PLATO_KB = 400;

/**
 * Reduce una foto a una miniatura WebP antes de subirla.
 *
 * Se hace en el navegador y no en el servidor a propósito: la foto de un
 * celular pesa 4–8 MB y no tiene sentido subirla entera para tirar el 99%.
 * Acá viaja ya en ~25 KB.
 *
 * Recorta al centro para llenar el cuadro: la tarjeta del POS muestra la foto
 * con `object-fit: cover`, así que si no se recorta acá se recorta allá igual,
 * y de paso se ahorran los bytes de lo que nunca se ve.
 */
export function reducirImagenPlato(file: File, lado = 480): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement('canvas');
      canvas.width = lado;
      canvas.height = Math.round(lado * 0.75);   // 4:3, la proporción de la tarjeta
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error(textoDe(textosCarta, 'navegadorNoProcesa')));
      // Recorte central tipo "cover".
      const escala = Math.max(canvas.width / img.width, canvas.height / img.height);
      const w = img.width * escala;
      const h = img.height * escala;
      ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      // 0.82 de calidad: por debajo se ven artefactos en la comida, por encima
      // el archivo crece sin que se note.
      const data = canvas.toDataURL('image/webp', 0.82);
      // Safari viejo no exporta WebP y devuelve un PNG; el backend lo acepta.
      resolve(data);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error(textoDe(textosCarta, 'noSeLeyoImagen'))); };
    img.src = url;
  });
}

export const subirImagenPlato = (productoId: number, dataUrl: string) =>
  api.put<{ ok: true; imagen_url: string; tamano: number; etag: string }>(
    `/api/menu/${productoId}/imagen`, { image: dataUrl },
  );

export const borrarImagenPlato = (productoId: number) =>
  api.del<{ ok: true }>(`/api/menu/${productoId}/imagen`);
